import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ConstantValues } from "../Constants/ConstantValues";
import { ValueSetType } from "../Constants/ValueSetType";
import { VaccinationRefusalModel } from "../Models/VaccinationRefusalModel";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { GetVaccinationRefusalResponse } from "../ResponseModels/GetVaccinationRefusalResponse";
import { BaseImmunizationTable, BaseImmunizationTableProps } from "./BaseImmunizationTable";

interface ImmunityRefusalTableProps extends BaseImmunizationTableProps<VaccinationRefusalModel> {
    onEndClick: (vaccinationRefusal: VaccinationRefusalModel) => void;
    onViewClick: (vaccinationRefusal: VaccinationRefusalModel) => void;
}

class ImmunizationRefusalTable extends BaseImmunizationTable<ImmunityRefusalTableProps, VaccinationRefusalModel> {
    protected title(): string {
        return cacheStore.GetString("QuebecVaccinationRegistry_VaccinationRefusalLabel");
    }

    protected tableId(): string {
        return "immunization-refusal-table";
    }

    private labels = {
        ImmunizationAgent: "QuebecVaccinationRegistry_AntigenImmunizingAgentLabel",
        Motive: "QuebecVaccinationRegistry_MotiveLabel",
        ApplicableFrom: "QuebecVaccinationRegistry_ApplicableFromLabel",
        ApplicableTo: "QuebecVaccinationRegistry_UntilLabel",
        Action: "ActionLabel"
    };

    private fieldNames = {
        ImmunizationAgent: "ImmunizationAgent",
        Motive: "Motive",
        ApplicableFrom: "ApplicableFrom",
        ApplicableTo: "ApplicableTo",
        Action: "Action"
    };

    protected defaultTableOptions(): Immutable.List<MyleTableColumnOptions> {
        return Immutable.List([
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ImmunizationAgent),
                FieldName: this.fieldNames.ImmunizationAgent,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Motive),
                FieldName: this.fieldNames.Motive,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ApplicableFrom),
                FieldName: this.fieldNames.ApplicableFrom,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ApplicableTo),
                FieldName: this.fieldNames.ApplicableTo,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Action),
                FieldName: this.fieldNames.Action,
                WidthPercentageDefault: 20
            })
        ]);
    }

    protected getTableColumns(tableOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<VaccinationRefusalModel>>(
            tableOptions,
            column => {
                switch (column.FieldName) {
                    case this.fieldNames.ImmunizationAgent:
                        return {
                            renderCell: item => (
                                <div>{item.Model.Vaccine.Code === ConstantValues.AllVaccinations
                                    ? cacheStore.GetString("QuebecVaccinationRegistry_Refusal_AllVaccines")
                                    : QuebecVaccinationRegistryHelper.translateValueSetString(item.Model.Vaccine, ValueSetType.Antigen)
                                }
                                </div>
                            )
                        };

                    case this.fieldNames.Motive:
                        return {
                            renderCell: item => (
                                <div>
                                    {QuebecVaccinationRegistryHelper.translateValueSetString(
                                        item.Model.Reason,
                                        ValueSetType.VaccinationRefusalMotive
                                    )}
                                </div>
                            )
                        };

                    case this.fieldNames.ApplicableFrom:
                        return {
                            renderCell: item => <div>{this.formatDate(item.Model.DatePeriod.Start)}</div>
                        };

                    case this.fieldNames.ApplicableTo:
                        return {
                            renderCell: item => <div>{this.formatDate(item.Model.DatePeriod.End)}</div>
                        };

                    case this.fieldNames.Action:
                        return {
                            renderCell: item => {
                                if (this.props.isReadOnly)
                                    return null;

                                if (item.Model.DatePeriod.End && !moment(item.Model.DatePeriod.End).isAfter(moment().startOf("day")))
                                    return (
                                        <a onClick={() => this.props.onViewClick(item.Model)}>
                                            {cacheStore.GetString("ViewLabel")}
                                        </a>
                                    );

                                return (
                                    <div>
                                        <a onClick={() => this.onEndImmunizationRefusal(item.Model)}>
                                            {cacheStore.GetString("QuebecVaccinationRegistry_EndDLabel")}
                                        </a>
                                    </div>
                                );
                            }
                        };
                }
            },
            false
        );
    }

    private async onEndImmunizationRefusal(item: VaccinationRefusalModel): Promise<void> {
        const response = await Client.postAsync<GetVaccinationRefusalResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_WithdrawVaccinationRefusal,
            { Id: item.Id }
        );
        if (response.isSuccess)
            this.props.onEndClick(response.data.VaccinationRefusal);
        else
            sessionStore.showPopup(response.errors);
    }
}

export { ImmunizationRefusalTable };
